import type { JSX, ComponentProps } from 'react'

import { Button as UiButton } from '../components/ui/button.tsx'
import { cn } from '../lib/cn.ts'

const variantMap = {
  primary: 'default',
  quiet: 'ghost',
  outline: 'outline',
  danger: 'destructive'
} as const

/**
 * House overrides of a shadcn variant's class string.
 *
 * shadcn's default variant hovers with `bg-primary/90`, which composites
 * toward the backdrop and so LIGHTENS the brand magenta: 4.65:1 under white
 * text at rest, 4.25:1 on hover, below the 4.5:1 floor. The Consumer Design
 * System darkens a primary action on hover instead
 * (`--color-action-primary-hover`, colours.css:58), and `--color-brand-soft`
 * is that step at 6.55:1. Only `primary` is affected; the danger button keeps
 * its own hover, which passes. EI-295.
 */
const variantClass = {
  primary: 'hover:bg-brand-soft',
  quiet: '',
  // shadcn's outline variant sets a border WIDTH and lets the base layer
  // colour it, which is `border-border` -- the decorative edge at 1.30:1 on a
  // card. colours.css:50-51 says that value must never bound an interactive
  // control, which is why `--input` moved to the control border; an outline
  // button is one too. `border-input` is the same token, at 5.35:1.
  outline: 'border-input',
  danger: ''
} as const

/**
 * House rules that apply to every variant.
 *
 * shadcn draws focus as an opaque `focus-visible:border-ring` plus a 50%
 * `focus-visible:ring-ring/50`. A filled button has no border width, so the
 * opaque half paints nothing and the indicator is the 50% ring alone: 2.31:1
 * on a card, under the 3:1 floor. An opaque ring is 4.65:1. EI-295 absorbed
 * EI-302 for this.
 */
const BASE_CLASS = 'focus-visible:ring-ring'

export type ButtonProps = Omit<ComponentProps<typeof UiButton>, 'variant'> & {
  'data-testid': string
  variant?: keyof typeof variantMap
}

/**
 * Button — the console's button.
 *
 * `data-testid` is required by the type, so the locator policy is enforced by
 * the compiler rather than by review. `type` defaults to `button`: a bare
 * button inside a form submits it, which is never what a console action means.
 *
 * Pass `asChild` with an `<a href>` inside when the action is really a
 * destination. The button then keeps its look and the reader keeps every
 * link behaviour — a new tab, a copied address, a URL on hover. `type` is
 * left off in that case, because it is not an attribute of an anchor.
 *
 * That omission has a sharp edge when the `asChild` child is itself a
 * `<button>`: nothing supplies a `type`, and HTML defaults a bare button to
 * `type="submit"`, so the child submits any form it sits inside. Such a
 * child must set its own `type`.
 *
 * @param props - Button props.
 * @param props.variant - Domain variant, mapped onto shadcn's.
 * @param props.type - Defaults to `button`, unless `asChild` is set.
 * @param props.asChild - Render the child element instead of a `<button>`.
 * @param props.className - Caller classes, which win over the house override.
 * @returns The rendered button.
 */
export function Button({
  variant = 'primary',
  type,
  asChild = false,
  className,
  ...props
}: ButtonProps): JSX.Element {
  return (
    <UiButton
      type={asChild ? type : (type ?? 'button')}
      asChild={asChild}
      variant={variantMap[variant]}
      className={cn(BASE_CLASS, variantClass[variant], className)}
      {...props}
    />
  )
}
